import React, { FC, useEffect, useState } from "react";
import { Editor } from "@monaco-editor/react";
import { Button, Flex, Modal, Notification, Spinner, Text } from "@telia/teddy";
import "./ValidateOrderModal.less";

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

interface ValidationResult {
    validationPassed: boolean | null;
    errors: string[];
    orderState: string | null;
}

const ValidateOrderModal: FC<Props> = ({ open, onOpenChange }) => {
    const [value, setValue] = useState("");
    const [submitState, setSubmitState] = useState<"idle" | "sending" | "error">("idle");
    const [errorMessage, setErrorMessage] = useState("");
    const [validationState, setValidationState] = useState<"idle" | "validating" | "done">("idle");
    const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
    const [isFetchingOrder, setIsFetchingOrder] = useState(false);

    useEffect(() => {
        if (!open) return;

        const fetchOrder = async () => {
            setIsFetchingOrder(true);
            try {
                const response = await fetch("/shop/devtools/order");
                const data = (await response.json()) as { rapidOrder?: unknown };
                if (data) {
                    setValue(JSON.stringify({ ...data }, null, 2));
                }
            } catch {
                // Silently fail if order fetch fails
            } finally {
                setIsFetchingOrder(false);
            }
        };

        fetchOrder();
    }, [open]);

    const handleClose = () => {
        setValue("");
        setSubmitState("idle");
        setErrorMessage("");
        setValidationState("idle");
        setValidationResult(null);
        onOpenChange(false);
    };

    const parseOrder = (): unknown | null => {
        try {
            const parsed = JSON.parse(value);
            return parsed;
        } catch {
            setErrorMessage("Ugyldig JSON. Sjekk at du har limt inn gyldig JSON.");
            return null;
        }
    };

    const handleValidateOrder = async () => {
        setSubmitState("sending");
        setValidationState("validating");
        setValidationResult(null);
        setErrorMessage("");

        const order = parseOrder();
        if (!order) {
            setSubmitState("error");
            setValidationState("done");
            return;
        }

        try {
            const response = await fetch("/shop/devtools/validate-pasted-order/", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ order }),
            });

            const body = (await response.json().catch(() => ({}))) as Partial<ValidationResult> & { error?: string };

            if (!response.ok) {
                setSubmitState("error");
                setErrorMessage(
                    typeof body.error === "string" ? body.error : `Request failed with status ${response.status}`,
                );
                setValidationState("done");
                return;
            }

            setValidationResult(body as ValidationResult);
            setSubmitState("idle");
            setValidationState("done");
        } catch (error) {
            setSubmitState("error");
            setErrorMessage(error instanceof Error ? error.message : "Noe gikk galt under validering. Prøv igjen.");
            setValidationState("done");
        }
    };

    return (
        <Modal open={open} onOpenChange={(next) => !next && handleClose()}>
            <Modal.Overlay className="validate-order-modal__overlay">
                <Modal.Content className="validate-order-modal" onOpenAutoFocus={(e) => e.preventDefault()} size="lg">
                    <Modal.Group variant="title">
                        <Modal.Title as="h3">Edit and validate order</Modal.Title>
                        <Modal.Close aria-label="lukk" slot="floating" />
                    </Modal.Group>
                    <Modal.Group direction="column">
                        {isFetchingOrder ? (
                            <div className="validate-order-modal__loading">
                                <Spinner size="lg" />
                            </div>
                        ) : (
                            <>
                                <Text mb="100">Below is your order in JSON format. You can edit and validate it.</Text>
                                <Flex gap="100" align="flex-start" mb="100">
                                    <div className="validate-order-modal__editor" style={{ flex: 1 }}>
                                        <Editor
                                            value={value}
                                            height="58vh"
                                            defaultLanguage="json"
                                            onChange={(newValue) => setValue(newValue ?? "")}
                                            options={{
                                                automaticLayout: true,
                                                minimap: { enabled: false },
                                                fontSize: 14,
                                                lineNumbersMinChars: 3,
                                                scrollBeyondLastLine: false,
                                                wordWrap: "on",
                                                tabSize: 2,
                                                formatOnPaste: true,
                                                formatOnType: true,
                                                stickyScroll: { enabled: false },
                                            }}
                                        />
                                        {(submitState === "error" || errorMessage) && (
                                            <Notification type="tile" variant="error">
                                                <Notification.Icon />
                                                <Notification.Text>{errorMessage}</Notification.Text>
                                            </Notification>
                                        )}
                                    </div>
                                    <Flex direction="column" mt="100" gapY="100" style={{ flexShrink: 0 }}>
                                        <Button
                                            variant="primary"
                                            width="9rem"
                                            onClick={handleValidateOrder}
                                            loading={validationState === "validating"}
                                            disabled={
                                                !value.trim() ||
                                                submitState === "sending" ||
                                                validationState === "validating"
                                            }
                                        >
                                            Validate
                                        </Button>
                                    </Flex>
                                </Flex>
                                {validationResult && validationState === "done" && (
                                    <>
                                        {validationResult.validationPassed ? (
                                            <Notification type="tile" variant="success">
                                                <Notification.Icon />
                                                <Notification.Text>Validation OK</Notification.Text>
                                            </Notification>
                                        ) : (
                                            <Notification type="tile" variant="error">
                                                <Notification.Icon />
                                                <Notification.Text>
                                                    <ul style={{ margin: "0.5rem 0", paddingLeft: "1.5rem" }}>
                                                        {validationResult?.errors?.map((error, idx) => (
                                                            <li key={idx}>{error}</li>
                                                        ))}
                                                    </ul>
                                                </Notification.Text>
                                            </Notification>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </Modal.Group>
                </Modal.Content>
            </Modal.Overlay>
        </Modal>
    );
};

export default ValidateOrderModal;
